/**
 * AdminBundles (T8.2) — admin bundle catalog management page.
 *
 * Pełny CRUD: tworzenie / edycja / soft-delete / restore.
 *
 * Model filtrowania „jak w katalogu modułów" (poprawki-develop): ZERO górnej
 * wyszukiwarki i górnych dropdownów filtrów — całe filtrowanie żyje w nagłówkach
 * kolumn (współdzielony `ConfigurableTable`, client-side na pobranej liście).
 * Kolumny wolnotekstowe (Kod/Nazwa/Moduły) mają szukajkę; kolumny cenowe/liczbowe
 * są nietekstowe (bez szukajki). Nad tabelą zostaje TYLKO kontrolka zmieniająca
 * pobierany zbiór („Pokaż skasowane") i przycisk „+ Dodaj pakiet".
 *
 * Ważne: cena pakietu wyliczana w locie z `module_pricing` to TYLKO admin preview.
 * Faktyczna cena tenant'a jest zamrażana w F4 — patrz banner w UI + ADR-0012.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Boxes } from 'lucide-react';

import { AdminBundleModal } from '@/components/AdminBundleModal';
import { AdminRestoreBundleModal } from '@/components/AdminRestoreBundleModal';
import { AdminSoftDeleteBundleModal } from '@/components/AdminSoftDeleteBundleModal';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { ConfigurableTable } from '@/components/configurable-table';
import type { ColumnDefinition } from '@/components/configurable-table/types';
import { Layout } from '@/components/Layout';
import { Skeleton } from '@/components/Skeleton';
import { PreviewTenantAuthBoundary } from '@/contexts/TenantAuthContext';
import { api } from '@/lib/api';

/** Górny limit pobrania — pod client-side filtry per kolumna. */
const LIST_FETCH_LIMIT = 100;

export interface BundleListItem {
  id: string;
  code: string;
  name: string | null;
  moduleCodes: string[];
  discountPercent: number;
  isActive: boolean;
  sumOfComponentsPlnMonthly: number | null;
  bundlePricePlnMonthly: number | null;
  sumOfComponentsPlnYearly: number | null;
  bundlePricePlnYearly: number | null;
  hasRetiredModules: boolean;
  missingPlnPricing: string[];
  missingYearlyPrices: string[];
  gcrecord: string | null;
  createdAt: string;
  updatedAt: string;
}

interface BundlesResponse {
  bundles: BundleListItem[];
}

function formatPln(grosze: number | null): string {
  if (grosze === null) return '—';
  const pln = grosze / 100;
  return pln.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function AdminBundles() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [softDeleteTarget, setSoftDeleteTarget] = useState<BundleListItem | null>(null);
  const [restoreTarget, setRestoreTarget] = useState<BundleListItem | null>(null);
  const [showDeleted, setShowDeleted] = useState(false);

  const query = useQuery({
    queryKey: ['admin', 'bundles', 'list', { showDeleted }],
    queryFn: async (): Promise<BundlesResponse> => {
      const params = new URLSearchParams();
      if (showDeleted) params.set('include_deleted', 'true');
      params.set('limit', String(LIST_FETCH_LIMIT));
      const res = await api.get<BundlesResponse>(`/admin/bundles?${params.toString()}`);
      return res.data;
    },
  });

  const onMutationSuccess = () => {
    setSoftDeleteTarget(null);
    setRestoreTarget(null);
    setCreateOpen(false);
    void queryClient.invalidateQueries({ queryKey: ['admin', 'bundles'] });
  };

  const columns = useMemo<ColumnDefinition<BundleListItem>[]>(
    () => [
      {
        id: 'code',
        accessor: 'code',
        header: t('admin.bundles.columns.code'),
        sortable: true,
        renderCell: (value) => <span className="font-mono text-xs">{String(value)}</span>,
      },
      {
        id: 'name',
        // Wolnotekstowa szukajka po nazwie (surowa wartość, null → pusty string).
        accessor: (row) => row.name ?? '',
        header: t('admin.bundles.columns.name'),
        sortable: true,
        renderCell: (_value, row) => (
          <span className="font-medium text-text-primary">{row.name ?? '—'}</span>
        ),
      },
      {
        id: 'modules',
        // Kody modułów złączone spacją — szukajka tekstowa działa substring po kodach.
        accessor: (row) => row.moduleCodes.join(' '),
        header: t('admin.bundles.columns.modules'),
        sortable: false,
        renderCell: (_value, row) => (
          <div className="flex flex-wrap items-center gap-1">
            {row.moduleCodes.map((c) => (
              <span key={c} className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-bg-tertiary">
                {c}
              </span>
            ))}
            {row.hasRetiredModules && (
              <Badge color="amber" data-testid={`bundle-retired-badge-${row.code}`}>
                {t('admin.bundles.retiredBadge')}
              </Badge>
            )}
          </div>
        ),
      },
      {
        id: 'sumMonthly',
        accessor: (row) => row.sumOfComponentsPlnMonthly,
        header: t('admin.bundles.columns.sumMonthly'),
        // Kolumna cenowa — bez szukajki (fraza nie ma sensu).
        filterable: false,
        sortable: false,
        align: 'right',
        renderCell: (_value, row) =>
          row.missingPlnPricing.length > 0 ? (
            <Badge color="red" data-testid={`bundle-broken-pln-${row.code}`}>
              {t('admin.bundles.brokenPlnBadge')}
            </Badge>
          ) : (
            <span>{formatPln(row.sumOfComponentsPlnMonthly)}</span>
          ),
      },
      {
        id: 'discount',
        accessor: (row) => row.discountPercent,
        header: t('admin.bundles.columns.discount'),
        filterable: false,
        sortable: false,
        align: 'right',
        renderCell: (_value, row) => <span>{row.discountPercent}%</span>,
      },
      {
        id: 'priceMonthly',
        accessor: (row) => row.bundlePricePlnMonthly,
        header: t('admin.bundles.columns.priceMonthly'),
        filterable: false,
        sortable: false,
        align: 'right',
        renderCell: (_value, row) => <span>{formatPln(row.bundlePricePlnMonthly)}</span>,
      },
      {
        id: 'priceYearly',
        accessor: (row) => row.bundlePricePlnYearly,
        header: t('admin.bundles.columns.priceYearly'),
        filterable: false,
        sortable: false,
        align: 'right',
        renderCell: (_value, row) => <span>{formatPln(row.bundlePricePlnYearly)}</span>,
      },
    ],
    [t],
  );

  const renderActions = (row: BundleListItem) => {
    const isDeleted = !!row.gcrecord;
    return (
      <div className="flex items-center justify-end gap-2">
        {isDeleted ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              setRestoreTarget(row);
            }}
          >
            {t('admin.bundles.actions.restore')}
          </Button>
        ) : (
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/bundles/${row.code}`);
              }}
            >
              {t('admin.bundles.actions.edit')}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setSoftDeleteTarget(row);
              }}
            >
              {t('admin.bundles.actions.softDelete')}
            </Button>
          </>
        )}
      </div>
    );
  };

  return (
    <Layout title={t('admin.bundles.title')}>
      <section className="px-6 py-8 flex flex-col gap-4">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-2xl font-semibold text-text-primary">{t('admin.bundles.title')}</h2>
            <p className="mt-1 text-sm text-text-tertiary">{t('admin.bundles.subtitle')}</p>
          </div>
          <div className="flex items-center gap-4">
            <Checkbox
              label={t('admin.bundles.filters.showDeleted')}
              checked={showDeleted}
              onChange={(e) => setShowDeleted(e.target.checked)}
            />
            <Button onClick={() => setCreateOpen(true)}>{t('admin.bundles.addButton')}</Button>
          </div>
        </div>

        <div
          role="note"
          data-testid="bundle-preview-banner"
          className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800 dark:bg-amber-900/20 dark:text-amber-200"
        >
          {t('admin.bundles.previewBanner')}
        </div>

        {query.isLoading ? (
          <div className="flex flex-col gap-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} height={48} />
            ))}
          </div>
        ) : query.isError ? (
          <div
            role="alert"
            className="rounded-xl border border-red-300 bg-red-50 p-6 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300"
          >
            <p>{t('admin.bundles.loadError')}</p>
            <Button
              variant="secondary"
              size="sm"
              className="mt-3"
              onClick={() => void query.refetch()}
            >
              {t('admin.bundles.retry')}
            </Button>
          </div>
        ) : (
          <PreviewTenantAuthBoundary>
            <ConfigurableTable<BundleListItem>
              tableId="admin-bundles"
              columns={columns}
              data={query.data?.bundles ?? []}
              getRowId={(row) => row.code}
              renderActions={renderActions}
              actionsColumnLabel={t('admin.bundles.columns.actions')}
              onRowClick={(row) => {
                // Klik w wiersz otwiera edycję; skasowane pakiety obsługuje przycisk „Przywróć".
                if (!row.gcrecord) navigate(`/bundles/${row.code}`);
              }}
              persistConfig={false}
              showViewModeToggle={false}
              emptyIcon={Boxes}
              emptyTitle={t('admin.bundles.empty')}
              emptyDescription=""
            />
          </PreviewTenantAuthBoundary>
        )}

        {createOpen && (
          <AdminBundleModal
            mode="create"
            onClose={() => setCreateOpen(false)}
            onSuccess={onMutationSuccess}
          />
        )}

        {softDeleteTarget && (
          <AdminSoftDeleteBundleModal
            bundleCode={softDeleteTarget.code}
            bundleName={softDeleteTarget.name ?? softDeleteTarget.code}
            onClose={() => setSoftDeleteTarget(null)}
            onSuccess={onMutationSuccess}
          />
        )}

        {restoreTarget && (
          <AdminRestoreBundleModal
            bundleCode={restoreTarget.code}
            bundleName={restoreTarget.name ?? restoreTarget.code}
            onClose={() => setRestoreTarget(null)}
            onSuccess={onMutationSuccess}
          />
        )}
      </section>
    </Layout>
  );
}
